import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { calculateRuntime, solveForBatterySize, solveForMaxLoad, calculateChargeTime, checkPassFail, generateDischargeCurve, compareScenarios, calculatePackTotals, runSensitivityAnalysis } from '@brt/shared-logic'
import type { BatterySlice } from './slices/batterySlice.ts'
import { createBatterySlice } from './slices/batterySlice.ts'
import type { LoadSlice } from './slices/loadSlice.ts'
import { createLoadSlice } from './slices/loadSlice.ts'
import type { EfficiencySlice } from './slices/efficiencySlice.ts'
import { createEfficiencySlice } from './slices/efficiencySlice.ts'
import type { SafetySlice } from './slices/safetySlice.ts'
import { createSafetySlice } from './slices/safetySlice.ts'
import type { ResultsSlice } from './slices/resultsSlice.ts'
import { createResultsSlice } from './slices/resultsSlice.ts'
import type { UISlice } from './slices/uiSlice.ts'
import { createUISlice } from './slices/uiSlice.ts'
import type { PreferencesSlice } from './slices/preferencesSlice.ts'
import { createPreferencesSlice } from './slices/preferencesSlice.ts'
import type { Scenario, ScenarioComparison, PackConfig, SavedBatteryProfile, SavedLoadProfile, HistoryEntry } from '@brt/shared-types'
import type { DemoDataset } from '@brt/shared-logic'

export interface ScenarioSlice {
  scenarios: Scenario[]
  scenarioComparison: ScenarioComparison | null
  savedBatteryProfiles: SavedBatteryProfile[]
  savedLoadProfiles: SavedLoadProfile[]
  calculationHistory: HistoryEntry[]
  addScenario: (scenario: Scenario) => void
  updateScenario: (id: string, updates: Partial<Scenario>) => void
  deleteScenario: (id: string) => void
  setScenarioComparison: (comparison: ScenarioComparison | null) => void
  saveBatteryProfile: (profile: SavedBatteryProfile) => void
  deleteBatteryProfile: (id: string) => void
  saveLoadProfile: (profile: SavedLoadProfile) => void
  deleteLoadProfile: (id: string) => void
  addToHistory: () => void
  loadFromHistory: (entryId: string) => void
  clearHistory: () => void
}

export interface PackSlice {
  pack: PackConfig | null
  setPackConfig: (pack: PackConfig | null) => void
  updatePackConfig: (updates: Partial<PackConfig>) => void
}

export interface ComputationSlice {
  computeAll: () => void
  computeRuntime: () => void
  computeBatterySize: (targetRuntimeHours: number) => void
  computeMaxLoad: (targetRuntimeHours: number) => void
  computeChargeTime: () => void
  computePassFail: () => void
  computeDischargeCurve: () => void
  computePack: () => void
  computeSensitivity: (variable: string, min: number, max: number, steps?: number) => void
  compareScenariosAction: () => void
}

export interface DemoSlice {
  loadDemoDataset: (dataset: DemoDataset) => void
  clearDemoData: () => void
}

export type AppStore = BatterySlice &
  LoadSlice &
  EfficiencySlice &
  SafetySlice &
  ResultsSlice &
  UISlice &
  PreferencesSlice &
  ScenarioSlice &
  PackSlice &
  ComputationSlice &
  DemoSlice

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      ...createBatterySlice(set as never, get as never),
      ...createLoadSlice(set as never, get as never),
      ...createEfficiencySlice(set as never),
      ...createSafetySlice(set as never),
      ...createResultsSlice(set as never),
      ...createUISlice(set as never),
      ...createPreferencesSlice(set as never),

      // --- Scenario Slice ---
      scenarios: [],
      scenarioComparison: null,
      savedBatteryProfiles: [],
      savedLoadProfiles: [],
      calculationHistory: [],

      addScenario: (scenario: Scenario) => set((state) => ({ scenarios: [...state.scenarios, scenario] })),
      updateScenario: (id: string, updates: Partial<Scenario>) =>
        set((state) => ({
          scenarios: state.scenarios.map((s: Scenario) => (s.id === id ? { ...s, ...updates } : s)),
        })),
      deleteScenario: (id: string) =>
        set((state) => ({ scenarios: state.scenarios.filter((s: Scenario) => s.id !== id) })),
      setScenarioComparison: (comparison: ScenarioComparison | null) => set({ scenarioComparison: comparison }),

      saveBatteryProfile: (profile: SavedBatteryProfile) =>
        set((state) => ({
          savedBatteryProfiles: [...state.savedBatteryProfiles, profile].slice(-25),
        })),
      deleteBatteryProfile: (id: string) =>
        set((state) => ({
          savedBatteryProfiles: state.savedBatteryProfiles.filter((p: SavedBatteryProfile) => p.id !== id),
        })),
      saveLoadProfile: (profile: SavedLoadProfile) =>
        set((state) => ({
          savedLoadProfiles: [...state.savedLoadProfiles, profile].slice(-25),
        })),
      deleteLoadProfile: (id: string) =>
        set((state) => ({
          savedLoadProfiles: state.savedLoadProfiles.filter((p: SavedLoadProfile) => p.id !== id),
        })),

      addToHistory: () => {
        const state = get()
        const entry: HistoryEntry = {
          id: `hist-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          timestamp: new Date().toISOString(),
          summary: state.runtimeResult?.runtimeFormatted ?? 'No result',
          battery: { ...state.battery },
          load: { ...state.load, loads: state.load.loads.map((l) => ({ ...l })) },
          efficiency: { ...state.efficiency },
          safety: { ...state.safety },
          solveMode: state.solveMode,
          runtimeResult: state.runtimeResult ? { ...state.runtimeResult } : null,
        }
        set((state) => ({
          calculationHistory: [entry, ...state.calculationHistory].slice(0, state.preferences.historyLimit),
        }))
      },

      loadFromHistory: (entryId: string) => {
        const state = get()
        const entry = state.calculationHistory.find((e: HistoryEntry) => e.id === entryId)
        if (!entry) return
        set({
          battery: { ...entry.battery },
          load: { ...entry.load, loads: entry.load.loads.map((l) => ({ ...l })) },
          efficiency: { ...entry.efficiency },
          safety: { ...entry.safety },
          solveMode: entry.solveMode,
          // Reset related state to avoid stale data
          pack: null,
          packResult: null,
          scenarioComparison: null,
          sensitivityResult: null,
          chargeTimeResult: null,
        })
        get().computeAll()
      },

      clearHistory: () => set({ calculationHistory: [] }),

      // --- Pack Slice ---
      pack: null,
      setPackConfig: (pack: PackConfig | null) => set({ pack }),
      updatePackConfig: (updates: Partial<PackConfig>) =>
        set((state) => ({
          pack: state.pack ? { ...state.pack, ...updates } : null,
        })),

      // --- Computation Slice (C-1: top-level, NOT in resultsSlice) ---
      computeRuntime: () => {
        const state = get()
        const result = calculateRuntime(state.battery, state.load, state.efficiency, state.safety)
        set({ runtimeResult: result })
      },

      computeBatterySize: (targetRuntimeHours: number) => {
        const state = get()
        const result = solveForBatterySize(
          targetRuntimeHours, state.load, state.efficiency, state.safety, state.battery,
        )
        set({ batterySizeResult: result })
      },

      computeMaxLoad: (targetRuntimeHours: number) => {
        const state = get()
        const result = solveForMaxLoad(
          targetRuntimeHours, state.battery, state.efficiency, state.safety,
        )
        set({ maxLoadResult: result })
      },

      computeChargeTime: () => {
        const state = get()
        const result = calculateChargeTime(state.battery, state.charger)
        set({ chargeTimeResult: result })
      },

      computePassFail: () => {
        const state = get()
        if (!state.safety.includeTargetCheck || state.runtimeResult === null) {
          set({ passFailResult: null })
          return
        }
        const result = checkPassFail(state.runtimeResult, state.safety.minimumRuntimeTarget)
        set({ passFailResult: result })
      },

      computeDischargeCurve: () => {
        const state = get()
        if (state.runtimeResult === null) {
          set({ dischargeCurve: [] })
          return
        }
        const points = generateDischargeCurve(
          state.battery.chemistry,
          state.runtimeResult.effectiveCapacityAh,
          state.runtimeResult.effectiveLoadA,
          state.preferences.dischargeCurveResolution,
          state.battery.voltage,
        )
        set({ dischargeCurve: points })
      },

      computePack: () => {
        const state = get()
        if (!state.pack) {
          set({ packResult: null })
          return
        }
        const result = calculatePackTotals(state.pack)
        set({ packResult: result })
      },

      computeSensitivity: (variable: string, min: number, max: number, steps = 20) => {
        const state = get()
        const result = runSensitivityAnalysis(
          state.battery, state.load, state.efficiency, state.safety,
          variable as 'temperature' | 'load' | 'efficiency' | 'dod' | 'soh',
          min, max, steps,
        )
        set({ sensitivityResult: result })
      },

      compareScenariosAction: () => {
        const state = get()
        if (state.scenarios.length === 0) {
          set({ scenarioComparison: null })
          return
        }
        const comparison = compareScenarios(
          state.battery, state.load, state.efficiency, state.safety, state.scenarios,
        )
        set({ scenarioComparison: comparison })
      },

      computeAll: () => {
        const state = get()
        const runtimeResult = calculateRuntime(state.battery, state.load, state.efficiency, state.safety)

        let passFailResult = null
        if (state.safety.includeTargetCheck && runtimeResult !== null) {
          passFailResult = checkPassFail(runtimeResult, state.safety.minimumRuntimeTarget)
        }

        let dischargeCurve: typeof state.dischargeCurve = []
        if (runtimeResult !== null) {
          dischargeCurve = generateDischargeCurve(
            state.battery.chemistry,
            runtimeResult.effectiveCapacityAh,
            runtimeResult.effectiveLoadA,
            state.preferences.dischargeCurveResolution,
            state.battery.voltage,
          )
        }

        const packResult = state.pack ? calculatePackTotals(state.pack) : null

        let batterySizeResult = null
        let maxLoadResult = null

        if (state.solveMode === 'BATTERY_SIZE' && state.safety.minimumRuntimeTarget > 0) {
          batterySizeResult = solveForBatterySize(
            state.safety.minimumRuntimeTarget, state.load, state.efficiency, state.safety, state.battery,
          )
        }

        if (state.solveMode === 'MAX_LOAD' && state.safety.minimumRuntimeTarget > 0) {
          maxLoadResult = solveForMaxLoad(
            state.safety.minimumRuntimeTarget, state.battery, state.efficiency, state.safety,
          )
        }

        const chargeTimeResult = calculateChargeTime(state.battery, state.charger)

        set({ runtimeResult, passFailResult, dischargeCurve, packResult, batterySizeResult, maxLoadResult, chargeTimeResult })
      },

      loadDemoDataset: (dataset: DemoDataset) => {
        set({
          battery: { ...dataset.battery },
          solveMode: dataset.solveMode,
          calculationMode: dataset.calculationMode,
          load: { ...dataset.load, loads: dataset.load.loads.map((l) => ({ ...l })) },
          charger: { ...dataset.charger },
          efficiency: { ...dataset.efficiency },
          safety: { ...dataset.safety },
          pack: dataset.pack ? { ...dataset.pack } : null,
          activeTab: 'runtime',
          quickModeActive: false,
          scenarioComparison: null,
          sensitivityResult: null,
        })
        get().computeAll()
      },

      clearDemoData: () => {
        set({
          battery: {
            chemistry: 'LI_FEPO4',
            capacityValue: 100,
            capacityUnit: 'AH',
            voltage: 12.8,
            dod: 0.80,
            stateOfCharge: 1.0,
            stateOfHealth: 1.0,
            temperatureC: 25,
            cutoffVoltage: 2.5,
            ageCycles: 0,
            storageDays: 0,
          },
          solveMode: 'RUNTIME',
          calculationMode: 'CURRENT_BASED',
          load: {
            loads: [{
              id: 'load-default',
              name: 'Load 1',
              loadValue: 5,
              loadUnit: 'A',
              enabled: true,
              isPreset: false,
            }],
          },
          charger: {
            chargerCurrentValue: 10,
            chargerVoltage: 12,
            chargerPower: 0,
            chargeEfficiency: 0.90,
          },
          efficiency: {
            efficiencyPercent: 0.93,
            includeEfficiency: false,
            description: 'DC-DC converter',
          },
          safety: {
            reserveMarginPercent: 0,
            includeReserve: false,
            minimumRuntimeTarget: 0,
            includeTargetCheck: false,
          },
          pack: null,
          scenarios: [],
          scenarioComparison: null,
          sensitivityResult: null,
          runtimeResult: null,
          passFailResult: null,
          dischargeCurve: [],
          packResult: null,
          batterySizeResult: null,
          maxLoadResult: null,
          chargeTimeResult: null,
        })
      },
    }),
    {
      name: 'runtime-lens-state',
      version: 3,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        version: 3,
        battery: state.battery,
        solveMode: state.solveMode,
        calculationMode: state.calculationMode,
        deviceMinVoltage: state.deviceMinVoltage,
        load: state.load,
        charger: state.charger,
        efficiency: state.efficiency,
        safety: state.safety,
        scenarios: state.scenarios,
        pack: state.pack,
        preferences: state.preferences,
        ui: {
          activeTab: state.activeTab,
          theme: state.theme,
          showFormulaExplanation: state.showFormulaExplanation,
          showAssumptionsSummary: state.showAssumptionsSummary,
          showAdvancedMode: state.showAdvancedMode,
          showLoadDemo: state.showLoadDemo,
        },
        savedBatteryProfiles: state.savedBatteryProfiles,
        savedLoadProfiles: state.savedLoadProfiles,
        calculationHistory: state.calculationHistory,
        calibration: state.calibration,
        userTier: state.userTier,
      }),
      migrate: (persisted, version) => {
        if (version < 3) {
          const state = persisted as Record<string, unknown>
          const prefs = state.preferences as Record<string, unknown> | undefined
          return {
            ...state,
            userTier: state.userTier ?? 'free',
            scenarios: state.scenarios ?? [],
            preferences: {
              defaultChemistry: prefs?.defaultChemistry ?? 'LI_FEPO4',
              defaultVoltage: prefs?.defaultVoltage ?? 12.8,
              defaultLoadPreset: prefs?.defaultLoadPreset ?? null,
              locale: prefs?.locale ?? 'en-US',
              temperatureUnit: prefs?.temperatureUnit ?? 'CELSIUS',
              dischargeCurveResolution: prefs?.dischargeCurveResolution ?? 100,
              showAdvancedModeDefault: prefs?.showAdvancedModeDefault ?? false,
              numberFormat: prefs?.numberFormat ?? 'system',
              showSecondsAlways: prefs?.showSecondsAlways ?? false,
              defaultEfficiency: prefs?.defaultEfficiency ?? 0.93,
              defaultReserveMargin: prefs?.defaultReserveMargin ?? 0.0,
              autoSaveProfiles: prefs?.autoSaveProfiles ?? true,
              historyLimit: prefs?.historyLimit ?? 50,
              exportFormat: prefs?.exportFormat ?? 'pdf',
              textSize: prefs?.textSize ?? 'normal',
              contrastMode: prefs?.contrastMode ?? 'normal',
            },
          }
        }
        return persisted
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.computeAll()
          if (typeof document !== 'undefined') {
            document.documentElement.dataset.theme = state.resolvedTheme
          }
        }
      },
    },
  ),
)

// Auto-compute when inputs change (D-1 fix)
useAppStore.subscribe(
  (state, prevState) => {
    const inputsChanged =
      state.battery !== prevState.battery ||
      state.load !== prevState.load ||
      state.efficiency !== prevState.efficiency ||
      state.safety !== prevState.safety ||
      state.solveMode !== prevState.solveMode

    if (inputsChanged) {
      // Use setTimeout to avoid calling setState during a setState
      setTimeout(() => useAppStore.getState().computeAll(), 0)
    }
  }
)
